import './notyf-DIw0RvBj.js';

var Klaviyo = new (class Klaviyo {
  /**
   * @type {string} email - The email of the user to subscribe
   * @type {string} marketHandle - The market handle of the user
   * @type {string} locale - The locale of the user
   * @type {string} listId - The list ID of the Klaviyo list to subscribe the user to
   * @description Subscribe a user to a Klaviyo list. Includes adding the manual_locale and market_handle properties to the user.
   */
  async subscribe(email, marketHandle, locale, listId) {
    const apiKey = cbt.klaviyoPublicApiKey;
    if (!apiKey) {
      console.error('Klaviyo public API key is not set');
      return { success: false, errors: ['Klaviyo public API key is not set'] };
    }

    if (!email || !marketHandle || !locale) {
      console.error('Email, market handle, and locale are required');
      return { success: false, errors: ['Email, market handle, and locale are required'] };
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions?company_id=${apiKey}`,
      {
        method: 'POST',
        headers: {
          revision: '2026-04-15',
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    properties: {
                      market_handle: marketHandle,
                      Manual_Language: locale,
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId,
                },
              },
            },
          },
        }),
      }
    );

    // If successful, Klaviyo doesn't return a response body. So we are done here.
    if (response.ok) {
      return { success: true, errors: [] };
    }

    try {
      const data = await response.json();
      if (data?.errors?.length > 0) {
        console.error('Error subscribing to Klaviyo:', data.errors);
        const readableErrors = data.errors.map((error) => {
          if (error.detail) return error.detail;
          return 'An unknown error occurred';
        });
        return { success: false, errors: readableErrors };
      }

      // Fallback if Klaviyo doesn't return in the manner that is expected.
      return { success: false, errors: [] };
    } catch (error) {
      console.error('Error subscribing to Klaviyo:', error);
      return { success: false, errors: [error instanceof Error ? error.message : `${error}`] };
    }
  }
})();

class NewsletterForm extends HTMLElement {
  #form = this.querySelector('form');
  #marketHandle = this.#form?.dataset?.marketHandle;
  #locale = this.#form?.dataset?.locale;
  #listId = this.#form?.dataset?.listId;
  #emailInput = this.#form?.querySelector('input[name="contact[email]"]');
  #emailErrorElement = this.#form?.querySelector('[data-email-error]');
  #genericErrorElement = this.#form?.querySelector('[data-generic-error]');
  #successMessageElement = this.querySelector('[data-success-form-submission]');

  connectedCallback() {
    this.addEventListener('submit', (event) => this.#onFormSubmit(event));
  }

  async #onFormSubmit(event) {
    event.preventDefault();
    this.#resetErrors();

    const email = this.#emailInput.value;
    if (!email) {
      this.#showError(this.#emailErrorElement, this.#emailInput);
      return;
    }

    if (!this.#marketHandle || !this.#locale) {
      this.#showError(this.#genericErrorElement);
      return;
    }

    const response = await Klaviyo.subscribe(
      email,
      this.#marketHandle,
      this.#locale.toUpperCase(),
      this.#listId
    );
    if (!response.success) {
      const emailHasError = response?.errors?.some((error) => error.includes('email'));
      if (emailHasError) {
        this.#showError(this.#emailErrorElement, this.#emailInput);
        return;
      }

      this.#showError(this.#genericErrorElement);
      return;
    }
    this.#showSuccess();
  }

  #resetErrors() {
    if (this.#emailErrorElement) {
      this.#emailErrorElement.classList.add('hidden');
      this.#emailErrorElement.setAttribute('aria-hidden', 'true');
    }
    if (this.#genericErrorElement) {
      this.#genericErrorElement.classList.add('hidden');
      this.#genericErrorElement.setAttribute('aria-hidden', 'true');
    }
  }

  #showError(element, elementToFocus) {
    element.classList.remove('hidden');
    element.setAttribute('aria-hidden', 'false');
    if (elementToFocus) {
      elementToFocus.focus();
    }
  }

  #showSuccess() {
    this.#form.remove();
    const successMessageElement = this.#successMessageElement.content.cloneNode(true);
    this.appendChild(successMessageElement);
  }
}

customElements.define('newsletter-form', NewsletterForm);
